import React, { useState } from "react";
import axios from "axios";
import { CalendarDays, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScheduleCallDialog() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "Schedule a call", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and preferred times.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, subject: form.subject || "Schedule a call" });
      toast.success("Request sent. Pablo's team will follow up with options.");
      setForm({ name: "", email: "", company: "", subject: "Schedule a call", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Could not send request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="hero-schedule-call"
          className="inline-flex items-center gap-2 bg-white text-black border border-gray-200 hover:border-black rounded-full px-7 py-3.5 text-sm font-medium transition-all"
        >
          <CalendarDays className="w-4 h-4" /> Schedule a call
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="schedule-call-dialog"
        className="max-w-lg bg-white rounded-3xl p-8 md:p-10 border-none"
      >
        <DialogHeader>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-gray-500">
            Executive intro · 30 minutes
          </div>
          <DialogTitle className="font-serif-display text-3xl md:text-4xl font-light text-black tracking-tight mt-2">
            Schedule a call
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Share two or three preferred windows — Pablo's team will confirm.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              data-testid="schedule-input-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Name"
              className="bg-[#F5F5F7] border-gray-200 rounded-xl h-12"
              required
            />
            <Input
              data-testid="schedule-input-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="Email"
              className="bg-[#F5F5F7] border-gray-200 rounded-xl h-12"
              required
            />
          </div>
          <Input
            data-testid="schedule-input-company"
            value={form.company}
            onChange={update("company")}
            placeholder="Company / organisation"
            className="bg-[#F5F5F7] border-gray-200 rounded-xl h-12"
          />
          <Textarea
            data-testid="schedule-input-message"
            value={form.message}
            onChange={update("message")}
            placeholder="Topic + two or three preferred times (timezone)"
            rows={5}
            className="bg-[#F5F5F7] border-gray-200 rounded-xl"
            required
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="schedule-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-black text-white hover:bg-[#1a1a1a] disabled:opacity-60 rounded-full px-7 py-3.5 text-sm font-medium transition-colors"
          >
            <Send className="w-4 h-4" />
            {loading ? "Sending…" : "Request invite"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
